'use client';

import { cn, composeTailwindRenderProps } from '@utils';
import {
	CheckboxGroup as CheckboxGroupPrimitive,
	Checkbox as CheckboxPrimitive,
} from 'react-aria-components';
import { FaCheck, FaMinus } from 'react-icons/fa';
import { tv } from 'tailwind-variants';

import type {
	CheckboxGroupProps as CheckboxGroupPrimitiveProps,
	CheckboxProps as CheckboxPrimitiveProps,
	ValidationResult,
} from 'react-aria-components';

import { ErrorFilled } from '../icons';
import {
	Description,
	errorMessageStyle,
	FieldError,
	FieldSuccess,
	iconStyle,
	Label,
} from './Field';

interface CheckboxOption<T> {
	label: React.ReactNode | string;
	value: T;
}

interface CheckboxGroupProps<T> extends CheckboxGroupPrimitiveProps {
	label?: string;
	hint?: string;
	hintChildren?: React.ReactNode;
	errorMessage?: string | ((validation: ValidationResult) => string);
	isValid?: boolean;
	successMessage?: string;
	options?: CheckboxOption<T>[];
	size?: 'small' | 'large' | 'auto';
}

const CheckboxGroup = <T,>({
	label,
	hint,
	hintChildren,
	errorMessage,
	isValid,
	successMessage,
	options,
	className,
	size,
	...props
}: CheckboxGroupProps<T>) => {
	return (
		<CheckboxGroupPrimitive
			{...props}
			className={composeTailwindRenderProps(className, 'flex flex-col gap-2')}
		>
			{label && <Label isRequired={props.isRequired}>{label}</Label>}
			{hint && <Description>{hint}</Description>}
			{hintChildren && hintChildren}
			{errorMessage && <FieldError>{errorMessage}</FieldError>}
			{isValid && successMessage && <FieldSuccess>{successMessage}</FieldSuccess>}
			{options && (
				<div className="flex flex-col gap-2">
					{options.map(option => (
						<Checkbox
							size={size}
							isValid={isValid}
							key={String(option.value)}
							value={option.value as unknown as string}
						>
							{option.label}
						</Checkbox>
					))}
				</div>
			)}
			{props.children as React.ReactNode}
		</CheckboxGroupPrimitive>
	);
};

const checkboxContainerStyles = tv({
	base: 'flex items-start gap-x-2',
	variants: {
		isHovered: {
			true: 'cursor-pointer',
		},
		isDisabled: {
			true: 'cursor-not-allowed opacity-50',
		},
	},
});

const boxStyles = tv({
	base: 'border-light-text flex shrink-0 items-center justify-center border-2',
	variants: {
		size: {
			small: 'size-5',
			large: 'size-7.5',
			auto: 'size-5 md:size-7.5',
		},
		isHovered: {
			true: 'bg-neutral-lightest cursor-pointer',
		},
		isSelected: {
			true: 'text-light-text',
		},
		isFocused: {
			true: 'outline-light-focus bg-neutral-lightest ring-0 outline-3 outline-offset-2',
		},
		isInvalid: {
			true: 'ring-status-error bg-status-error-lightest ring-2 ring-offset-0',
		},
		isValid: {
			true: 'ring-status-success bg-status-success-lighter ring-2 ring-offset-0',
		},
		isDisabled: {
			true: 'bg-neutral-lightest',
		},
	},
	compoundVariants: [
		{
			isInvalid: true,
			isFocused: true,
			className: 'bg-neutral-lightest ring-transparent',
		},
		{
			isValid: true,
			isFocused: true,
			className: 'bg-neutral-lightest ring-transparent',
		},
	],
	defaultVariants: {
		size: 'auto',
	},
});

const iconStyles = tv({
	base: 'size-3',
	variants: {
		size: {
			small: 'size-3',
			large: 'size-4',
			auto: 'size-3 md:size-4',
		},
	},
	defaultVariants: {
		size: 'auto',
	},
});

interface CheckboxProps extends CheckboxPrimitiveProps {
	label?: string;
	checkboxLabel?: string;
	hint?: string;
	errorMessage?: string | ((validation: ValidationResult) => string);
	description?: string;
	size?: 'small' | 'large' | 'auto';
	isValid?: boolean;
}

const Checkbox = ({
	label,
	checkboxLabel,
	hint,
	errorMessage,
	className,
	size,
	isValid,
	...props
}: CheckboxProps) => {
	return (
		<CheckboxPrimitive {...props} className={composeTailwindRenderProps(className, 'w-fit')}>
			{({ isSelected, isIndeterminate, ...renderProps }) => (
				<div className="flex w-fit flex-col gap-2">
					{label && <Label isRequired={props.isRequired}>{label}</Label>}
					{hint && <Description>{hint}</Description>}
					{errorMessage && (
						<p className={cn(errorMessageStyle({ theme: 'light' }))}>
							<ErrorFilled className={iconStyle({ theme: 'light', isInvalid: true })} />
							{errorMessage as string}
						</p>
					)}
					<div
						className={cn(
							checkboxContainerStyles({
								...renderProps,
							}),
						)}
					>
						<div
							className={boxStyles({
								size,
								...renderProps,
								isSelected: isSelected || isIndeterminate,
								isValid: isSelected && isValid,
							})}
						>
							{isIndeterminate ? (
								<FaMinus className={iconStyles({ size })} />
							) : isSelected ? (
								<FaCheck className={iconStyles({ size })} />
							) : null}
						</div>

						<div className="-mt-0.5 flex flex-col gap-1 md:mt-0.5">
							<>
								{checkboxLabel ? (
									<Label className={cn(props.description && 'text-sm/4 font-normal')}>
										{checkboxLabel}
									</Label>
								) : (
									(props.children as React.ReactNode)
								)}
								{props.description && <Description>{props.description}</Description>}
							</>
						</div>
					</div>
				</div>
			)}
		</CheckboxPrimitive>
	);
};

export type { CheckboxGroupProps, CheckboxProps };
export { Checkbox, CheckboxGroup };
